import { Reveal } from "@/components/Reveal";

interface ImageTextSectionProps {
  id?: string;
  image?: string;
  video?: string;
  eyebrow?: string;
  title: React.ReactNode;
  subtitle?: string;
  description: React.ReactNode;
  imagePosition?: "left" | "right";
  imageAlt?: string;
  className?: string;
  imageClassName?: string;
  variant?: "split" | "background" | "container";
  hasBorderTop?: boolean;
  hasBorderBottom?: boolean;
  paddingBottom?: "none" | "small" | "default";
}

export function ImageTextSection({
  id,
  image,
  video,
  eyebrow,
  title,
  subtitle,
  description,
  imagePosition = "left",
  imageAlt,
  className = "",
  imageClassName = "",
  variant = "split",
  hasBorderTop = false,
  hasBorderBottom = true,
  paddingBottom = "default",
}: ImageTextSectionProps) {
  const finalImageAlt = imageAlt || (typeof title === "string" ? title : "");
  const pbClass =
    paddingBottom === "none"
      ? "pb-0"
      : paddingBottom === "small"
        ? "pb-10 lg:pb-12"
        : "pb-20 lg:pb-24";

  if (variant === "container") {
    const imageElement = (
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0b0b0b] w-full h-[320px] md:h-[480px] lg:h-[580px] flex items-center justify-center">
        <Reveal
          effect={imagePosition === "left" ? "left" : "right"}
          className="w-full h-full flex items-center justify-center"
        >
          {video ? (
            <video autoPlay muted loop playsInline className="w-full h-full object-cover">
              <source src={video} type="video/mp4" />
            </video>
          ) : (
            image && <img src={image} alt={finalImageAlt} className={`w-full h-full object-cover ${imageClassName}`} />
          )}
        </Reveal>
      </div>
    );

    const contentElement = (
      <Reveal effect={imagePosition === "left" ? "right" : "left"} className="space-y-8">
        {eyebrow && (
          <span className="text-lg font-bold uppercase tracking-[0.25em] text-accent">
            {eyebrow}
          </span>
        )}

        <div className="space-y-4">
          <h2 className="display text-3xl uppercase tracking-[0.15em] text-white md:text-4xl">
            {title}
          </h2>

          {subtitle && <p className="text-lg leading-relaxed text-white/70">{subtitle}</p>}
        </div>

        <div className="text-white/80 leading-relaxed font-light text-base md:text-lg">
          {description}
        </div>
      </Reveal>
    );

    return (
      <section
        id={id}
        className={`relative overflow-hidden ${hasBorderTop ? "border-t border-accent" : ""} ${hasBorderBottom ? "border-b border-accent" : ""} bg-black text-white ${className}`}
      >
        <div
          className={`container-bizu pt-20 lg:pt-24 ${pbClass} grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}
        >
          {imagePosition === "left" ? (
            <>
              {imageElement}
              {contentElement}
            </>
          ) : (
            <>
              <div className="order-2 lg:order-1">{contentElement}</div>
              <div className="order-1 lg:order-2">{imageElement}</div>
            </>
          )}
        </div>
      </section>
    );
  }

  const imageClasses =
    imagePosition === "left"
      ? "lg:col-start-1 lg:col-end-3 lg:border-r"
      : "lg:col-start-3 lg:col-end-5 lg:border-l";

  const contentClasses =
    variant === "background"
      ? "lg:col-start-2 lg:col-end-4"
      : imagePosition === "left"
        ? "lg:col-start-3 lg:col-end-4 lg:pl-20"
        : "lg:col-start-2 lg:col-end-3 lg:pr-20";
  return (
    <section
      id={id}
      className={`relative overflow-hidden ${hasBorderTop ? "border-t border-accent" : ""} ${hasBorderBottom ? "border-b border-accent" : ""} bg-black text-white ${className}`}
    >
      {variant === "background" && (
        <div className="absolute inset-0 w-full h-full">
          {video ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={video} type="video/mp4" />
            </video>
          ) : (
            image && (
              <img
                src={image}
                alt={finalImageAlt}
                className={`absolute inset-0 w-full h-full object-cover ${imageClassName}`}
              />
            )
          )}
          <div className="absolute inset-0 bg-black/70" />
        </div>
      )}

      <div className="relative z-10 grid w-full grid-cols-1 lg:grid-cols-[1fr_minmax(0,700px)_minmax(0,700px)_1fr]">
        {variant === "split" && (
          <div
            className={`relative min-h-[350px] overflow-hidden border-b border-accent lg:min-h-full lg:border-b-0 lg:row-start-1 ${imageClasses}`}
          >
            <Reveal effect={imagePosition === "left" ? "left" : "right"} className="h-full w-full">
              {video ? (
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 h-full w-full object-cover"
                >
                  <source src={video} type="video/mp4" />
                </video>
              ) : (
                image && (
                  <img
                    src={image}
                    alt={finalImageAlt}
                    className={`absolute inset-0 h-full w-full object-cover ${imageClassName}`}
                  />
                )
              )}
            </Reveal>
          </div>
        )}

        <div
          className={`flex items-center px-[clamp(1.25rem,4vw,2.5rem)] pt-20 lg:pt-24 ${pbClass} lg:row-start-1 ${contentClasses}`}
        >
          <Reveal
            effect={imagePosition === "left" ? "right" : "left"}
            className="max-w-2xl space-y-8"
          >
            {eyebrow && (
              <span className="text-lg font-bold uppercase tracking-[0.25em] text-accent">
                {eyebrow}
              </span>
            )}

            <div className="space-y-4">
              <h2 className="display text-3xl uppercase tracking-[0.15em] text-white md:text-4xl">
                {title}
              </h2>

              {subtitle && <p className="text-lg leading-relaxed text-white/70">{subtitle}</p>}
            </div>

            <div className="text-muted-foreground leading-relaxed">{description}</div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
